import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useAuth } from '../auth/AuthProvider';
import { readBootstrap, saveBootstrap, db } from '../offline/db';
import {
  demoBootstrap,
  demoClients,
  demoDashboard,
  demoInvoices,
  demoOrders,
  demoRates,
  demoIntegrations,
} from '../data/demo';
import type {
  BootstrapData,
  Cliente,
  DashboardSummary,
  Factura,
  IntegracionEstado,
  ListResponse,
  Pedido,
  Tasa,
} from './types';

export interface PedidosFiltros {
  estado?: string;
  desde?: string;
  hasta?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface FacturasFiltros {
  estado?: string;
  search?: string;
  page?: number;
  limit?: number;
}

function paginate<T>(items: T[], page = 1, limit = 25): ListResponse<T> {
  const total = items.length;
  const offset = (page - 1) * limit;
  return {
    data: items.slice(offset, offset + limit),
    meta: {
      page,
      limit,
      total,
      pages: Math.max(1, Math.ceil(total / limit)),
    },
  };
}

function matchSearch(term: string, ...values: Array<string | null | undefined>): boolean {
  if (!term) return true;
  const needle = term.toLowerCase();
  return values.some((value) => value?.toLowerCase().includes(needle));
}

function filterDemoOrders(filtros: PedidosFiltros): Pedido[] {
  const term = filtros.search ?? '';
  return demoOrders.filter((pedido) => {
    if (filtros.estado && pedido.estado !== filtros.estado) return false;
    const fecha = pedido.createdAt.slice(0, 10);
    if (filtros.desde && fecha < filtros.desde) return false;
    if (filtros.hasta && fecha > filtros.hasta) return false;
    return matchSearch(term, pedido.numero, pedido.clienteNombre, pedido.clienteRif);
  });
}

function filterDemoInvoices(filtros: FacturasFiltros): Factura[] {
  const term = filtros.search ?? '';
  return demoInvoices.filter((factura) => {
    if (filtros.estado && factura.estado !== filtros.estado) return false;
    return matchSearch(
      term,
      factura.numero,
      factura.clienteNombre,
      factura.clienteRif,
    );
  });
}

export function useBootstrap() {
  const { user, demo } = useAuth();
  return useQuery<BootstrapData>({
    queryKey: ['bootstrap', demo ? 'demo' : (user?.id ?? 'anon'), 'v2'],
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (demo) return demoBootstrap;
      try {
        const { data } = await api.bootstrap();
        await saveBootstrap(data);
        return data;
      } catch (error) {
        const cached = await readBootstrap();
        if (cached) return { ...cached, offline: true };
        throw error;
      }
    },
  });
}

export function useRates() {
  const { user, demo } = useAuth();
  return useQuery<Tasa[]>({
    queryKey: ['rates', demo ? 'demo' : (user?.id ?? 'anon')],
    enabled: Boolean(user),
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      if (demo) return demoRates;
      try {
        const { data } = await api.tasas.list();
        await db.rates.bulkPut(data);
        return data;
      } catch (error) {
        const cached = await db.rates.toArray();
        if (cached.length > 0) return cached;
        throw error;
      }
    },
  });
}

export function useDashboard() {
  const { user, demo } = useAuth();
  return useQuery<DashboardSummary>({
    queryKey: ['dashboard', demo ? 'demo' : (user?.id ?? 'anon')],
    enabled: Boolean(user),
    staleTime: 30 * 1000,
    queryFn: async () => {
      if (demo) return demoDashboard;
      const { data } = await api.dashboard();
      return data;
    },
  });
}

export function usePedidos(filtros: PedidosFiltros) {
  const { user, demo } = useAuth();
  return useQuery<ListResponse<Pedido>>({
    queryKey: ['pedidos', demo ? 'demo' : (user?.id ?? 'anon'), filtros],
    enabled: Boolean(user),
    queryFn: async () => {
      if (demo) {
        return { ...paginate(filterDemoOrders(filtros), filtros.page, filtros.limit), demo: true };
      }
      return api.pedidos.list(filtros);
    },
  });
}

export function useFacturas(filtros: FacturasFiltros) {
  const { user, demo } = useAuth();
  return useQuery<ListResponse<Factura>>({
    queryKey: ['facturas', demo ? 'demo' : (user?.id ?? 'anon'), filtros],
    enabled: Boolean(user),
    queryFn: async () => {
      if (demo) {
        return {
          ...paginate(filterDemoInvoices(filtros), filtros.page, filtros.limit),
          demo: true,
        };
      }
      return api.facturas.list(filtros);
    },
  });
}

export function useClientes(search: string) {
  const { user, demo } = useAuth();
  return useQuery<ListResponse<Cliente>>({
    queryKey: ['clientes', demo ? 'demo' : (user?.id ?? 'anon'), search],
    enabled: Boolean(user),
    queryFn: async () => {
      if (demo) {
        const term = search.toLowerCase();
        const filtered = demoClients.filter(
          (cliente) =>
            !term ||
            cliente.nombre.toLowerCase().includes(term) ||
            cliente.rif.toLowerCase().includes(term),
        );
        return { ...paginate(filtered, 1, 100), demo: true };
      }
      return api.clientes.list({ search, limit: 100 });
    },
  });
}

export function useIntegraciones() {
  const { user, demo } = useAuth();
  return useQuery<IntegracionEstado[]>({
    queryKey: ['integraciones', demo ? 'demo' : (user?.id ?? 'anon')],
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (demo) return demoIntegrations;
      const { data } = await api.integraciones();
      return data.integrations;
    },
  });
}

export function useCrearCliente() {
  const { demo } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (demo) throw new Error('La creación de clientes no está disponible en modo demo.');
      return api.clientes.create(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clientes'] });
      void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}

export function useCrearTasa() {
  const { demo } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { moneda: string; tasa: number; fuente?: string }) => {
      if (demo) throw new Error('La carga de tasas no está disponible en modo demo.');
      return api.tasas.crear(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rates'] });
      void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}

export function useSincronizarTasas() {
  const { demo } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (demo) throw new Error('El conector BCV no está disponible en modo demo.');
      return api.tasas.sync();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rates'] });
      void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
      void queryClient.invalidateQueries({ queryKey: ['integraciones'] });
    },
  });
}

export function useCambiarEstadoPedido() {
  const { demo } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      estado,
      motivo,
    }: {
      id: string;
      estado: string;
      motivo?: string;
    }) => {
      if (demo) throw new Error('El cambio de estado no está disponible en modo demo.');
      return api.pedidos.cambiarEstado(id, estado, motivo);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useTasaDia(fecha: string | undefined) {
  const { user, demo } = useAuth();
  return useQuery({
    queryKey: ['tasas', 'dia', fecha ?? 'hoy'],
    enabled: Boolean(user) && !demo && Boolean(fecha),
    queryFn: () => api.tasas.dia(fecha ?? ''),
    staleTime: 1000 * 60 * 60,
  });
}

export function useClienteResumen(id: string | null) {
  const { user, demo } = useAuth();
  return useQuery({
    queryKey: ['clientes', 'resumen', id],
    enabled: Boolean(user) && !demo && Boolean(id),
    queryFn: async () => (await api.clientes.resumen(id ?? '')).data,
  });
}

export function useActualizarCliente() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.clientes.update(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clientes'] });
      void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}
